import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Upload, FileSpreadsheet, FileDown, CheckCircle2,
  AlertCircle, School, GraduationCap, ArrowRight,
  Building2, Loader2, X, FileText, UserPlus,
  Calendar, Landmark, ArrowLeft, Users, PartyPopper
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { schoolApi, cyclesApi, levelsApi, classesApi, studentsApi } from '@/services/api'

interface Section { id: number; code: string; label_fr: string; label_en: string }
interface Cycle { id: number; section_id: number; code: string; label_fr: string; label_en: string; order: number }
interface Level { id: number; cycle_id: number; code: string; label_fr: string; label_en: string; order: number }
interface ClassItem { id: number; level_id: number; specialty_id: number; code: string; abbreviation: string; name: string; capacity: number; status: string }
interface SchoolItem { id: number; name: string; acronym: string; city: string; status: string }
interface AcademicYear { id: number; school_id: number; label: string; start_date: string; end_date: string; is_current: boolean; status: string }

function cn(...classes: (string | false | null | undefined)[]) { return classes.filter(Boolean).join(' ') }

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const AUTH_STORAGE_KEY = 'auth-storage'

const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY)
    if (sid) return Number(sid)
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e) }
  return null
}

const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yid) return Number(yid)
  } catch (e) { console.warn('getStoredYearId error:', e) }
  return null
}

const getStoredYearLabel = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
  } catch (e) { return null }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE CSV — FORMAT STANDARD (Nom et Prénom séparés)
// ═══════════════════════════════════════════════════════════════════════════════
const TEMPLATE_HEADERS = [
  'Matricule',
  "Nom de l'élève",
  "Prénom de l'élève",
  'Date de naissance (AAAA-MM-JJ)',
  'Sexe (M/F)',
  'Lieu de naissance',
  'Nationalité',
  'Nom du Père',
  'Nom de la Mère',
  "Téléphone d'urgence",
  "N° de référence de l'acte de naissance",
  "Centre d'Etat Civil",
  "Emetteur de l'acte de naissance",
  'Handicap (Oui/Non)',
  'Cas Social (Oui/Non)',
  'Redoublant (Oui/Non)',
]

const TEMPLATE_EXAMPLE = [
  ['2025-001', 'KIBINDE OMBIONYO', 'Franck Landry', '2010-05-15', 'M', 'Douala', 'Camerounaise', 'Awang Betrand', 'Awang Mary', '+237 677123456', 'BC-2025-001', 'Kumba II Council', 'Civil Status Registrar', 'Non', 'Non', 'Non'],
  ['2025-002', 'ATEBA', 'Marie Claire', '2011-03-22', 'F', 'Yaoundé', 'Camerounaise', 'ATEBA Jean', 'ATEBA Rose', '+237 677654321', 'BC-2025-002', 'Yaoundé I Council', 'Civil Status Registrar', 'Non', 'Oui', 'Oui'],
]

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE 2 — NOM ET PRÉNOM FUSIONNÉS
// RÈGLE MÉTIER (V4) : le nom complet est enregistré dans le champ
// last_name (Nom) UNIQUEMENT — le champ first_name (Prénom) reste vide.
// L'affichage "{last_name} {first_name}" reste donc correct.
// ═══════════════════════════════════════════════════════════════════════════════
const TEMPLATE_MERGED_HEADERS = [
  'Matricule',
  "Nom et Prénom de l'élève",
  'Date de naissance (AAAA-MM-JJ)',
  'Sexe (M/F)',
  'Lieu de naissance',
  'Nationalité',
  'Nom du Père',
  'Nom de la Mère',
  "Téléphone d'urgence",
  "N° de référence de l'acte de naissance",
  "Centre d'Etat Civil",
  "Emetteur de l'acte de naissance",
  'Handicap (Oui/Non)',
  'Cas Social (Oui/Non)',
  'Redoublant (Oui/Non)',
]

const TEMPLATE_MERGED_EXAMPLE = [
  ['2025-001', 'KIBINDE OMBIONYO Franck Landry', '2010-05-15', 'M', 'Douala', 'Camerounaise', 'Awang Betrand', 'Awang Mary', '+237 677123456', 'BC-2025-001', 'Kumba II Council', 'Civil Status Registrar', 'Non', 'Non', 'Non'],
  ['2025-002', 'ATEBA Marie Claire', '2011-03-22', 'F', 'Yaoundé', 'Camerounaise', 'ATEBA Jean', 'ATEBA Rose', '+237 677654321', 'BC-2025-002', 'Yaoundé I Council', 'Civil Status Registrar', 'Non', 'Oui', 'Oui'],
]

// ═══════════════════════════════════════════════════════════════════════════════
// V5 — NORMALISATION DES EN-TÊTES CSV
// Excel remplace souvent l'apostrophe droite (') par une apostrophe typographique
// (’) et ajoute des espaces / variations d'accents dans les en-têtes. Sans
// normalisation, "Nom et Prénom de l’élève" ≠ "Nom et Prénom de l'élève"
// → la conversion était SILENCIEUSEMENT IGNORÉE → le fichier fusionné partait
// tel quel au backend → DOUBLONS first_name / last_name.
// ═══════════════════════════════════════════════════════════════════════════════
function NORM(h: string): string {
  return h
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')        // retire les accents (é → e, è → e...)
    .replace(/[\u2018\u2019\u02bc`]/g, "'") // apostrophes typographiques → '
    .replace(/\s+/g, ' ')                     // espaces multiples → un seul
    .trim()
    .toLowerCase()
}

// En-têtes canoniques du modèle : forme normalisée → libellé officiel
const CANON_BY_NORM: Record<string, string> = {}
TEMPLATE_HEADERS.forEach(h => { CANON_BY_NORM[NORM(h)] = h })
CANON_BY_NORM[NORM("Nom et Prénom de l'élève")] = "Nom et Prénom de l'élève"
CANON_BY_NORM[NORM('Nom')] = 'Nom'
CANON_BY_NORM[NORM('Prenom')] = 'Prenom'
CANON_BY_NORM[NORM('Prénom')] = 'Prénom'

const NORM_MERGED_COL = NORM("Nom et Prénom de l'élève")

function downloadTemplateCSV() {
  const BOM = '\uFEFF'
  const csv = BOM + [
    TEMPLATE_HEADERS.join(';'),
    ...TEMPLATE_EXAMPLE.map(r => r.join(';'))
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'modele_import_eleves.csv'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
  toast.success('Modèle CSV téléchargé')
}


function downloadTemplateMergedCSV() {
  const BOM = '\uFEFF'
  const csv = BOM + [
    TEMPLATE_MERGED_HEADERS.join(';'),
    ...TEMPLATE_MERGED_EXAMPLE.map(r => r.join(';'))
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = 'modele_import_eleves_fusionne.csv'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
  toast.success('Modèle CSV fusionné téléchargé')
}

// ═══════════════════════════════════════════════════════════════════════════════
// V4 — CORRECTION DU FORMAT FUSIONNÉ
// Le backend parse le fichier CSV brut. Avec le modèle fusionné, il enregistrait
// le nom complet à la fois dans last_name ET first_name (doublon).
// Solution : on retransforme le fichier fusionné en fichier standard AVANT
// l'envoi, en plaçant le nom complet dans la colonne "Nom de l'élève"
// (→ champ last_name) et en laissant "Prénom de l'élève" vide (→ first_name vide).
// ═══════════════════════════════════════════════════════════════════════════════
function parseCSVLine(line: string, separator: string): string[] {
  const cells: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { current += '"'; i++ }
        else { inQuotes = false }
      } else {
        current += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === separator) {
      cells.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  cells.push(current)
  return cells
}

function csvEscape(value: string, separator: string): string {
  const s = String(value ?? '')
  if (s.includes(separator) || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * V5 — Transforme un fichier CSV au format fusionné ("Nom et Prénom de l'élève")
 * en fichier CSV au format standard où :
 *   - "Nom de l'élève"    = nom complet   → enregistré dans last_name
 *   - "Prénom de l'élève" = vide          → first_name vide (plus de doublon)
 *
 * Détection des colonnes NORMALISÉE (apostrophes ’/‘, accents, casse, espaces)
 * pour fonctionner avec les fichiers sauvegardés via Excel.
 *
 * LÈVE une erreur si la conversion est impossible — on préfère ABORTER
 * l'import plutôt que d'envoyer le fichier fusionné original qui recrée
 * des doublons dans la base.
 */
async function buildStandardFileFromMerged(file: File): Promise<File> {
  const text = await file.text()
  let clean = text.charCodeAt(0) === 0xFEFF ? text.substring(1) : text
  clean = clean.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = clean.split('\n').filter(l => l.trim())
  if (!lines.length) throw new Error('Fichier vide')

  const firstLine = lines[0]
  const separator = firstLine.includes('\t') ? '\t' : (firstLine.includes(';') ? ';' : ',')
  const headers = parseCSVLine(firstLine, separator).map(h => h.trim().replace(/^\uFEFF/, ''))
  const normHeaders = headers.map(NORM)

  // Colonne fusionnée : détection normalisée (tolère ’ ‘, accents, espaces)
  const mergedIdx = normHeaders.indexOf(NORM_MERGED_COL)
  if (mergedIdx === -1) {
    throw new Error("Colonne \"Nom et Prénom de l'élève\" introuvable dans le fichier")
  }

  // Index de chaque colonne du modèle standard dans le fichier (correspondance normalisée)
  const stdIdx: Record<string, number> = {}
  TEMPLATE_HEADERS.forEach(h => {
    const idx = normHeaders.indexOf(NORM(h))
    if (idx !== -1) stdIdx[h] = idx
  })

  const outLines: string[] = [TEMPLATE_HEADERS.join(';')]

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i], separator)
    const mergedName = (cells[mergedIdx] ?? '').trim()
    if (!mergedName) continue // ligne sans nom = ligne vide/invalide

    // Construire la ligne au format standard : colonnes communes recopiées
    // (correspondance normalisée), nom complet dans "Nom de l'élève",
    // "Prénom de l'élève" laissé VIDE.
    const stdRow: Record<string, string> = {}
    TEMPLATE_HEADERS.forEach(h => {
      stdRow[h] = stdIdx[h] !== undefined ? (cells[stdIdx[h]] ?? '').trim() : ''
    })
    stdRow["Nom de l'élève"] = mergedName   // ← nom complet dans last_name uniquement
    stdRow["Prénom de l'élève"] = ''        // ← plus de doublon : first_name reste vide

    outLines.push(TEMPLATE_HEADERS.map(h => csvEscape(stdRow[h], ';')).join(';'))
  }

  if (outLines.length <= 1) {
    throw new Error('Aucune ligne convertible dans le fichier fusionné')
  }

  const BOM = '\uFEFF'
  return new File(
    [BOM + outLines.join('\n')],
    file.name.replace(/\.csv$/i, '_standard.csv'),
    { type: 'text/csv;charset=utf-8;' }
  )
}

/**
 * V6 — Normalise les fins de ligne d'un fichier CSV : \r\n (Excel/Windows)
 * et \r (Mac classique) → \n (LF).
 *
 * POURQUOI : le backend lit le CSV via io.StringIO SANS newline=''. Le module
 * csv de Python lève alors "_csv.Error: new-line character seen in unquoted
 * field - do you need to open the file with newline=''?" sur tout fichier
 * sauvegardé par Excel (CRLF) → 500 Internal Server Error à l'import.
 * En envoyant du LF pur, le csv.reader du backend fonctionne correctement.
 */
async function normalizeLineEndings(f: File): Promise<File> {
  const text = await f.text()
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  if (normalized === text) return f // déjà en LF, rien à faire
  console.log('[Import] Fins de ligne normalisées (CRLF/CR → LF) pour compatibilité csv backend')
  return new File([normalized], f.name, { type: f.type || 'text/csv' })
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function StudentImportPage() {
  const { lang } = useLang()
  const navigate = useNavigate()

  const [sections, setSections] = useState<Section[]>([])
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [levels, setLevels] = useState<Level[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [schools, setSchools] = useState<SchoolItem[]>([])
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([])
  const [loadingRef, setLoadingRef] = useState(true)

  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId)
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId)
  const [schoolName, setSchoolName] = useState('')
  const [academicYearName, setAcademicYearName] = useState(getStoredYearLabel() || '')

  const [sectionId, setSectionId] = useState('')
  const [cycleId, setCycleId] = useState('')
  const [levelId, setLevelId] = useState('')
  const [classId, setClassId] = useState('')

  const [file, setFile] = useState<File | null>(null)
  const [isMergedFormat, setIsMergedFormat] = useState(false)
  const [previewData, setPreviewData] = useState<any[]>([])
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ success: number; errors: any[]; total: number } | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  // ═══════════════════════════════════════════════════════════════════════════════
  // CHARGEMENT DES DONNÉES
  // ═══════════════════════════════════════════════════════════════════════════════
  const loadRefData = useCallback(async () => {
    setLoadingRef(true)
    try {
      let detectedSchoolId = schoolId
      let detectedSchoolName = schoolName

      if (!detectedSchoolId) {
        const schoolsData = await schoolApi.getSchools()
        const schoolsList = Array.isArray(schoolsData) ? schoolsData : []
        setSchools(schoolsList)

        const detectedSchool = schoolsList.find((s: SchoolItem) => s.status === 'active') || schoolsList[0]
        if (detectedSchool) {
          detectedSchoolId = detectedSchool.id
          detectedSchoolName = detectedSchool.name
          setSchoolId(detectedSchoolId)
          setSchoolName(detectedSchoolName)
          localStorage.setItem(SELECTED_SCHOOL_ID_KEY, String(detectedSchoolId))
        }
      } else {
        const schoolsData = await schoolApi.getSchools()
        const schoolsList = Array.isArray(schoolsData) ? schoolsData : []
        setSchools(schoolsList)
        const found = schoolsList.find((s: SchoolItem) => s.id === detectedSchoolId)
        if (found) {
          detectedSchoolName = found.name
          setSchoolName(detectedSchoolName)
        }
      }

      let detectedYearId = academicYearId
      let detectedYearName = academicYearName

      if (detectedSchoolId) {
        const yearsData = await schoolApi.getAcademicYears(detectedSchoolId)
        const yearsList = Array.isArray(yearsData) ? yearsData : []
        setAcademicYears(yearsList)

        if (!detectedYearId) {
          const currentYear = yearsList.find((y: AcademicYear) => y.is_current) || yearsList[0]
          if (currentYear) {
            detectedYearId = currentYear.id
            detectedYearName = currentYear.label
            setAcademicYearId(detectedYearId)
            setAcademicYearName(detectedYearName)
            localStorage.setItem(SELECTED_YEAR_ID_KEY, String(detectedYearId))
            localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detectedYearName || '')
          }
        } else {
          const foundYear = yearsList.find((y: AcademicYear) => y.id === detectedYearId)
          if (foundYear) {
            detectedYearName = foundYear.label
            setAcademicYearName(detectedYearName)
          } else {
            const currentYear = yearsList.find((y: AcademicYear) => y.is_current) || yearsList[0]
            if (currentYear) {
              detectedYearId = currentYear.id
              detectedYearName = currentYear.label
              setAcademicYearId(detectedYearId)
              setAcademicYearName(detectedYearName)
              localStorage.setItem(SELECTED_YEAR_ID_KEY, String(detectedYearId))
              localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detectedYearName)
            }
          }
        }

        const secData = await schoolApi.getSections(detectedSchoolId)
        setSections(Array.isArray(secData) ? secData : [])
      }

      const [cycData, levData, clsData] = await Promise.allSettled([
        cyclesApi.getAll(),
        levelsApi.getAll(),
        classesApi.getAll()
      ])
      const cyclesRaw = cycData.status === 'fulfilled' ? (Array.isArray(cycData.value) ? cycData.value : []) : []
      const levelsRaw = levData.status === 'fulfilled' ? (Array.isArray(levData.value) ? levData.value : []) : []
      const classesRaw = clsData.status === 'fulfilled' ? (Array.isArray(clsData.value) ? clsData.value : []) : []

      setCycles(cyclesRaw)
      setLevels(levelsRaw)
      setClasses(classesRaw)

    } catch (e) {
      console.error('Erreur chargement:', e)
      toast.error(lang === 'fr' ? 'Erreur chargement des données' : 'Error loading data')
    } finally {
      setLoadingRef(false)
    }
  }, [lang, schoolId, academicYearId])

  useEffect(() => { loadRefData() }, [loadRefData])

  // ─── ÉCOUTE DES CHANGEMENTS D'ANNÉE ───
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId && newYearId !== academicYearId) {
          setAcademicYearId(newYearId)
          const label = localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || ''
          setAcademicYearName(label)
          toast.info(lang === 'fr' ? 'Année scolaire mise à jour' : 'Academic year updated')
        }
      }
      if (e.key === SELECTED_SCHOOL_ID_KEY) {
        const newSchoolId = e.newValue ? Number(e.newValue) : null
        if (newSchoolId && newSchoolId !== schoolId) {
          setSchoolId(newSchoolId)
          toast.info(lang === 'fr' ? 'Établissement mis à jour' : 'School updated')
        }
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [lang, academicYearId, schoolId])

  // ─── FILTRAGE CASCADÉ ───
  const filteredCycles = sectionId
    ? cycles.filter((c: Cycle) => c.section_id === Number(sectionId))
    : []

  const filteredLevels = cycleId
    ? levels.filter((l: Level) => l.cycle_id === Number(cycleId))
    : sectionId
      ? levels.filter((l: Level) => {
          const c = cycles.find((cc: Cycle) => cc.id === l.cycle_id)
          return c && c.section_id === Number(sectionId)
        })
      : []

  const filteredClasses = levelId
    ? classes.filter((c: ClassItem) => c.level_id === Number(levelId))
    : cycleId
      ? classes.filter((c: ClassItem) => {
          const lvl = levels.find((l: Level) => l.id === c.level_id)
          return lvl && lvl.cycle_id === Number(cycleId)
        })
      : []

  const selectedClass = classes.find((c: ClassItem) => c.id === Number(classId))

  // ─── UPLOAD FICHIER ───
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error(lang === 'fr' ? 'Format non supporté. Utilisez un fichier CSV' : 'Unsupported format. Use CSV')
      return
    }
    setFile(f)
    setImportResult(null)
    setShowSuccessModal(false)
    parseFile(f)
  }

  const parseFile = async (f: File) => {
    try {
      const text = await f.text()
      console.log('[CSV Parse] Raw text length:', text.length)
      console.log('[CSV Parse] Raw first 200 chars:', text.substring(0, 200))

      let cleanText = text
      if (cleanText.charCodeAt(0) === 0xFEFF) {
        cleanText = cleanText.substring(1)
        console.log('[CSV Parse] BOM removed')
      }

      const normalizedText = cleanText.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

      const firstLine = normalizedText.split('\n')[0] || ''
      console.log('[CSV Parse] First line:', firstLine)

      const separator = firstLine.includes('\t') ? '\t' : (firstLine.includes(';') ? ';' : ',')
      console.log('[CSV Parse] Separator detected:', separator)

      const lines = normalizedText.split('\n').filter(l => l.trim())
      console.log('[CSV Parse] Non-empty lines:', lines.length)
      console.log('[CSV Parse] First line raw:', JSON.stringify(lines[0]))

      if (lines.length < 2) {
        toast.error(lang === 'fr' ? 'Fichier vide ou invalide' : 'Empty or invalid file')
        setPreviewData([])
        setIsMergedFormat(false)
        return
      }

      let detectedSeparator = separator
      if (lines[0].includes('\t')) {
        detectedSeparator = '\t'
        console.log('[CSV Parse] Tab separator detected!')
      }

      const rawHeaders = lines[0].split(detectedSeparator)
      const headers = rawHeaders.map(h => {
        let trimmed = h.trim()
        if (trimmed.charCodeAt(0) === 0xFEFF) {
          trimmed = trimmed.substring(1)
        }
        return trimmed
      })

      console.log('[CSV Parse] Headers:', headers)
      console.log('[CSV Parse] Header count:', headers.length)

      // V5 : détection NORMALISÉE du format fusionné (tolère ’ ‘, accents, casse, espaces)
      const mergedDetected = headers.some(h => NORM(h) === NORM_MERGED_COL)
      setIsMergedFormat(mergedDetected)
      if (mergedDetected) {
        console.log('[CSV Parse] FORMAT FUSIONNÉ détecté (détection normalisée) → le nom complet sera enregistré dans last_name (Nom) uniquement')
      }

      const rows = []
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue

        const cells = line.split(detectedSeparator)
        // Clés CANONIQUES : chaque en-tête du fichier est associé à l'en-tête
        // officiel du modèle via comparaison normalisée. row contient donc
        // TOUJOURS les clés standard, quel que soit l'apostrophe ou les accents
        // utilisés dans le fichier Excel.
        const row: Record<string, string> = {}
        headers.forEach((h, idx) => {
          const canon = CANON_BY_NORM[NORM(h)]
          if (canon && row[canon] === undefined) row[canon] = cells[idx]?.trim() || ''
        })

        const hasMatricule = row['Matricule']?.trim()
        // V5 : supporte les deux formats (séparé et fusionné)
        const hasNom = row["Nom de l'élève"]?.trim() || row['Nom']?.trim()
        const hasPrenom = row["Prénom de l'élève"]?.trim() || row['Prénom']?.trim() || row['Prenom']?.trim()
        const hasNomPrenomFusionne = row["Nom et Prénom de l'élève"]?.trim()

        if (hasMatricule || hasNom || hasPrenom || hasNomPrenomFusionne) {
          if (hasNomPrenomFusionne && !hasNom && !hasPrenom) {
            // ═══ V5 — CORRECTION DU DOUBLON ═══
            // Le nom complet va dans "Nom de l'élève" (→ champ last_name)
            // et "Prénom de l'élève" reste VIDE (→ champ first_name vide).
            row["Nom de l'élève"] = hasNomPrenomFusionne
            row["Prénom de l'élève"] = ''
          }
          rows.push(row)
        }
      }

      console.log('[CSV Parse] Valid rows with data:', rows.length)
      if (rows.length > 0) {
        console.log('[CSV Parse] First row:', rows[0])
      }

      const rowsWithId = rows.map((row, idx) => ({ ...row, _rowId: idx + 1 }))
      setPreviewData(rowsWithId)

      if (rowsWithId.length === 0) {
        toast.error(lang === 'fr' ? 'Aucun élève trouvé dans le fichier. Vérifiez le format.' : 'No students found in file. Check format.')
      } else {
        toast.success(`${rowsWithId.length} ${lang === 'fr' ? 'élèves détectés dans le fichier' : 'students detected in file'}`)
      }
    } catch (e) {
      console.error('[CSV Parse] Error:', e)
      toast.error(lang === 'fr' ? 'Erreur lecture du fichier' : 'Error reading file')
    }
  }

  // ─── IMPORT — V3 : conversion du fichier fusionné avant envoi ───
  const handleImport = async () => {
    if (!classId) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner une classe' : 'Please select a class')
      return
    }
    if (!file) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner un fichier' : 'Please select a file')
      return
    }
    if (!schoolId) {
      toast.error(lang === 'fr' ? "Établissement non détecté" : 'School not detected')
      return
    }
    if (!academicYearId) {
      toast.error(lang === 'fr' ? "Année scolaire non détectée" : 'Academic year not detected')
      return
    }

    setImporting(true)
    setShowSuccessModal(false)
    try {
      const formData = new FormData()
      formData.append('class_id', classId)
      formData.append('school_id', String(schoolId))
      formData.append('academic_year_id', String(academicYearId))

      if (sectionId) formData.append('section_id', sectionId)
      if (cycleId) formData.append('cycle_id', cycleId)
      if (levelId) formData.append('level_id', levelId)

      // ═══ V6 — NORMALISATION DES FINS DE LIGNE (TOUS les fichiers) ═══
      // Le backend lit le CSV via io.StringIO sans newline='' → tout fichier
      // Excel (CRLF) provoque "_csv.Error: new-line character seen in unquoted
      // field" (500). On convertit donc systématiquement en LF avant l'envoi.
      let fileToSend: File = file
      try {
        fileToSend = await normalizeLineEndings(file)
      } catch (normErr) {
        console.error('[Import] Normalisation des fins de ligne impossible:', normErr)
        // On continue avec le fichier original : la conversion fusionnée
        // ci-dessous normalise aussi ses propres fins de ligne.
      }

      // ═══ V4 — CORRECTION DU FORMAT FUSIONNÉ ═══
      // Si le fichier est au format fusionné, on le convertit côté client en
      // format standard (nom complet dans "Nom de l'élève", "Prénom" vide)
      // AVANT de l'envoyer au backend. Le backend enregistre ainsi le nom
      // complet dans last_name UNIQUEMENT — plus de doublon first_name.
      if (isMergedFormat) {
        // Conversion OBLIGATOIRE : si elle échoue, on ABANDONNE l'import.
        // Envoyer le fichier fusionné original recréerait des doublons
        // (nom complet dans first_name ET last_name côté backend).
        try {
          fileToSend = await buildStandardFileFromMerged(file)
        } catch (convErr: any) {
          console.error('[Import] Conversion fusionné→standard impossible:', convErr)
          toast.error(lang === 'fr'
            ? `Import annulé : conversion du fichier fusionné impossible (${convErr?.message || convErr}). Aucun élève n'a été importé — corrigez le fichier et réessayez.`
            : `Import cancelled: cannot convert merged file (${convErr?.message || convErr}). No student was imported.`)
          setImporting(false)
          return
        }
        const convertedText = await fileToSend.text()
        console.log('[Import] Format fusionné → fichier converti (nom complet → last_name, first_name vide)')
        console.log('[Import] Contenu converti (3 premières lignes):\n' + convertedText.split('\n').slice(0, 3).join('\n'))
        toast.info(lang === 'fr'
          ? 'Format fusionné : le nom complet sera enregistré dans Nom (last_name) uniquement'
          : 'Merged format: the full name will be saved in Last name (last_name) only')
      }
      formData.append('file', fileToSend)

      console.log('[Import] Sending with:', {
        class_id: classId,
        school_id: schoolId,
        academic_year_id: academicYearId,
        section_id: sectionId,
        cycle_id: cycleId,
        level_id: levelId,
        file_sent: fileToSend.name,
        merged_converted: isMergedFormat && fileToSend !== file,
      })

      const res = await studentsApi.importStudents(formData)
      setImportResult(res)

      if (res.success > 0) {
        toast.success(`${res.success} ${lang === 'fr' ? 'élèves importés avec succès' : 'students imported successfully'}`)
        setShowSuccessModal(true)
      }
      if (res.errors?.length > 0) {
        toast.error(`${res.errors.length} ${lang === 'fr' ? 'erreurs' : 'errors'}`)
      }
    } catch (e: any) {
      console.error('Import error:', e)
      toast.error(e?.response?.data?.detail || (lang === 'fr' ? 'Erreur import' : 'Import error'))
    } finally {
      setImporting(false)
    }
  }

  const handleGoToStudents = () => {
    setShowSuccessModal(false)
    navigate('/students')
  }

  const handleNewImport = () => {
    setShowSuccessModal(false)
    resetForm()
  }

  const resetForm = () => {
    setSectionId('')
    setCycleId('')
    setLevelId('')
    setClassId('')
    setFile(null)
    setIsMergedFormat(false)
    setPreviewData([])
    setImportResult(null)
    setShowSuccessModal(false)
  }

  if (loadingRef) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/20 to-amber-50/20 relative">
      {/* ✅ POPUP DE CONFIRMATION */}
      {showSuccessModal && importResult && importResult.success > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <PartyPopper className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">
                {lang === 'fr' ? 'Importation réussie !' : 'Import Successful!'}
              </h3>
              <p className="text-emerald-100 text-sm mt-1">
                {lang === 'fr'
                  ? `${importResult.success} élève${importResult.success > 1 ? 's' : ''} importé${importResult.success > 1 ? 's' : ''} avec succès`
                  : `${importResult.success} student${importResult.success > 1 ? 's' : ''} imported successfully`}
              </p>
            </div>
            <div className="p-6">
              {importResult.errors.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-sm text-amber-700 font-medium flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    {importResult.errors.length} {lang === 'fr' ? 'ligne(s) non enregistrée(s)' : 'row(s) not saved'} :
                  </p>
                  <div className="max-h-40 overflow-y-auto text-xs text-amber-800 space-y-1 pr-1">
                    {importResult.errors.map((err: any, i: number) => {
                      // Infos élève : champs backend en priorité, repli sur le fichier (previewData)
                      const src = previewData[err.row - 2] || {}
                      const m = err.matricule || src['Matricule'] || ''
                      const merged = src["Nom et Prénom de l'élève"] || ''
                      const n = err.nom || src["Nom de l'élève"] || src['Nom'] || (merged ? merged : '')
                      const p = err.prenom || src["Prénom de l'élève"] || src['Prénom'] || ''
                      const label = [m && `[${m}]`, n, p].filter(Boolean).join(' ')
                      return (
                        <div key={i} className="flex items-baseline gap-2 border-b border-amber-200/40 pb-1">
                          <span className="font-bold text-amber-900 whitespace-nowrap shrink-0">Ligne {err.row}</span>
                          {label && <span className="font-semibold text-amber-800 whitespace-nowrap shrink-0">{label}</span>}
                          <span className="text-amber-700">{err.error}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              <p className="text-sm text-gray-600 text-center mb-6">
                {lang === 'fr'
                  ? 'Les élèves ont été enregistrés dans la base de données. Souhaitez-vous consulter la liste des élèves ou effectuer un nouvel import ?'
                  : 'Students have been saved to the database. Would you like to view the student list or perform another import?'}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGoToStudents}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-200"
                >
                  <Users className="w-5 h-5" />
                  {lang === 'fr' ? 'Voir la liste des élèves' : 'View Student List'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={handleNewImport}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Upload className="w-5 h-5 text-gray-500" />
                  {lang === 'fr' ? 'Nouvel import' : 'New Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-purple-700 to-purple-900 bg-clip-text text-transparent">
                {lang === 'fr' ? 'Importer la liste des élèves' : 'Import Student List'}
              </h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">
                {lang === 'fr' ? 'Importer des élèves via CSV' : 'Import students via CSV'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Info Établissement & Année */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-blue-50 to-blue-100/50 border-b border-blue-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" />
              {lang === 'fr' ? 'Contexte scolaire (auto-détecté)' : 'School Context (auto-detected)'}
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <School className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{lang === 'fr' ? 'Établissement' : 'School'}</p>
                  <p className="text-sm font-semibold text-blue-800">{schoolName || '—'}</p>
                  <p className="text-xs text-blue-600">ID: {schoolId || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{lang === 'fr' ? 'Année scolaire' : 'Academic Year'}</p>
                  <p className="text-sm font-semibold text-amber-800">{academicYearName || '—'}</p>
                  <p className="text-xs text-amber-600">ID: {academicYearId || 'N/A'}</p>
                </div>
              </div>
            </div>
            {(!schoolId || !academicYearId) && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-700">
                  {lang === 'fr'
                    ? "⚠️ Établissement ou année scolaire non détectés. Vérifiez que des écoles et années académiques existent dans le système."
                    : '⚠️ School or academic year not detected. Please verify schools and academic years exist in the system.'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Step 1: Select Class */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-purple-50 to-purple-100/50 border-b border-purple-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <School className="w-5 h-5 text-purple-600" />
              {lang === 'fr' ? 'Étape 1 : Sélectionner la classe' : 'Step 1: Select Class'}
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {lang === 'fr' ? 'Section' : 'Section'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <School className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={sectionId}
                    onChange={(e) => { setSectionId(e.target.value); setCycleId(''); setLevelId(''); setClassId('') }}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 appearance-none"
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {sections.map((s: Section) => (
                      <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {lang === 'fr' ? 'Cycle' : 'Cycle'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={cycleId}
                    onChange={(e) => { setCycleId(e.target.value); setLevelId(''); setClassId('') }}
                    disabled={!sectionId || filteredCycles.length === 0}
                    className={cn(
                      'w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 appearance-none',
                      (!sectionId || filteredCycles.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200'
                    )}
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {filteredCycles.map((c: Cycle) => (
                      <option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {lang === 'fr' ? 'Niveau' : 'Level'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <ArrowRight className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={levelId}
                    onChange={(e) => { setLevelId(e.target.value); setClassId('') }}
                    disabled={!cycleId || filteredLevels.length === 0}
                    className={cn(
                      'w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 appearance-none',
                      (!cycleId || filteredLevels.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200'
                    )}
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {filteredLevels.map((l: Level) => (
                      <option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {lang === 'fr' ? 'Classe' : 'Class'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={classId}
                    onChange={(e) => setClassId(e.target.value)}
                    disabled={!levelId || filteredClasses.length === 0}
                    className={cn(
                      'w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 appearance-none',
                      (!levelId || filteredClasses.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200'
                    )}
                  >
                    <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                    {filteredClasses.map((c: ClassItem) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {selectedClass && (
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-purple-800 font-medium">
                  {lang === 'fr' ? 'Classe sélectionnée' : 'Selected class'} : {selectedClass.name}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Download Template */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              {lang === 'fr' ? 'Étape 2 : Télécharger le modèle' : 'Step 2: Download Template'}
            </h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              {lang === 'fr'
                ? "Téléchargez le modèle CSV, complétez-le avec les informations des élèves, puis importez-le."
                : 'Download the CSV template, fill it with student information, then import it.'}
            </p>
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {lang === 'fr' ? 'Champs du fichier modèle standard (17 champs)' : 'Standard template fields (17 fields)'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {TEMPLATE_HEADERS.map((h, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <div className="w-5 h-5 bg-emerald-100 rounded flex items-center justify-center text-emerald-700 font-bold text-[10px]">
                      {i + 1}
                    </div>
                    <span className="truncate">{h}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={downloadTemplateCSV}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                <FileDown className="w-4 h-4 text-green-600" />
                {lang === 'fr' ? 'Modèle standard (Nom + Prénom séparés)' : 'Standard template (Last + First name separate)'}
              </button>
              <button
                onClick={downloadTemplateMergedCSV}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                <FileDown className="w-4 h-4 text-blue-600" />
                {lang === 'fr' ? 'Modèle fusionné (Nom et Prénom ensemble)' : 'Merged template (Full name combined)'}
              </button>
            </div>
            {/* V3 : note explicative sur le format fusionné */}
            <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
              <p className="text-xs text-blue-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {lang === 'fr'
                  ? "📌 Modèle fusionné : le nom complet (ex. « KIBINDE OMBIONYO Franck Landry ») sera enregistré dans le champ Nom (last_name) uniquement. Le champ Prénom (first_name) restera vide — plus de doublon. L'affichage « Nom Prénom » reste identique."
                  : '📌 Merged template: the full name (e.g. "KIBINDE OMBIONYO Franck Landry") will be saved in the Last name (last_name) field only. The First name (first_name) field stays empty — no more duplication. The "Last First" display stays identical.'}
              </p>
            </div>
          </div>
        </div>

        {/* Step 3: Upload & Import */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-blue-50 to-blue-100/50 border-b border-blue-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              {lang === 'fr' ? 'Étape 3 : Importer le fichier CSV' : 'Step 3: Import CSV File'}
            </h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Zone upload */}
            <div
              className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center transition-all',
                file ? 'border-blue-400 bg-blue-50/50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              )}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-10 h-10 text-blue-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        {file.name}
                        {isMergedFormat && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded-full">
                            {lang === 'fr' ? 'FORMAT FUSIONNÉ' : 'MERGED FORMAT'}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); setFile(null); setPreviewData([]); setImportResult(null); setShowSuccessModal(false); setIsMergedFormat(false) }}
                      className="p-1 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="font-medium text-gray-700">
                      {lang === 'fr' ? 'Cliquez pour sélectionner un fichier CSV' : 'Click to select a CSV file'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Format accepté : .csv (séparateur ; ou ,)
                    </p>
                  </>
                )}
              </label>
            </div>

            {/* V3 : bandeau informatif format fusionné */}
            {file && isMergedFormat && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl">
                <p className="text-xs text-purple-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  {lang === 'fr'
                    ? "Format fusionné détecté ✅ — à l'importation, le nom complet sera enregistré dans le champ Nom (last_name) uniquement. Aucun doublon ne sera créé."
                    : 'Merged format detected ✅ — on import, the full name will be saved in the Last name (last_name) field only. No duplication will be created.'}
                </p>
              </div>
            )}

            {/* Preview */}
            {previewData.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {previewData.length} {lang === 'fr' ? 'élèves à importer' : 'students to import'}
                  </span>
                  <span className="text-xs text-blue-600 font-medium">
                    {lang === 'fr' ? 'Aperçu des 5 premiers' : 'Preview of first 5'}
                  </span>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left font-medium text-gray-500">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Matricule</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Nom</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Prénom</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Date naiss.</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Sexe</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Lieu</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {previewData.slice(0, 5).map((row, i) => (
                        <tr key={row._rowId || i}>
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2 font-medium text-blue-700">{row['Matricule'] || '-'}</td>
                          <td className="px-3 py-2">{row["Nom de l'élève"] || <span className="text-gray-300">—</span>}</td>
                          <td className="px-3 py-2">{row["Prénom de l'élève"] || '-'}</td>
                          <td className="px-3 py-2">{row['Date de naissance (AAAA-MM-JJ)'] || '-'}</td>
                          <td className="px-3 py-2">{row['Sexe (M/F)'] || '-'}</td>
                          <td className="px-3 py-2">{row['Lieu de naissance'] || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 5 && (
                    <p className="text-xs text-gray-500 text-center py-2">
                      +{previewData.length - 5} {lang === 'fr' ? 'autres élèves' : 'more students'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Résultat import */}
            {importResult && (
              <div className={cn(
                'rounded-xl p-4 border',
                importResult.errors.length === 0
                  ? 'bg-green-50 border-green-200'
                  : 'bg-amber-50 border-amber-200'
              )}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.errors.length === 0 ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  )}
                  <span className="font-medium text-gray-800">
                    {importResult.success} {lang === 'fr' ? 'élèves importés' : 'students imported'}
                  </span>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-amber-700 font-medium">
                      {importResult.errors.length} {lang === 'fr' ? 'ligne(s) non enregistrée(s)' : 'row(s) not saved'} :
                    </p>
                    <div className="max-h-48 overflow-y-auto text-xs text-amber-800 space-y-1 pr-1">
                      {importResult.errors.map((err: any, i: number) => {
                        // Infos élève : priorité aux champs renvoyés par le backend,
                        // repli sur les données du fichier (previewData, index = ligne - 2)
                        const src = previewData[err.row - 2] || {}
                        const m = err.matricule || src['Matricule'] || ''
                        const merged = src["Nom et Prénom de l'élève"] || ''
                        const n = err.nom || src["Nom de l'élève"] || src['Nom'] || (merged ? merged : '')
                        const p = err.prenom || src["Prénom de l'élève"] || src['Prénom'] || ''
                        const label = [m && `[${m}]`, n, p].filter(Boolean).join(' ')
                        return (
                          <div key={i} className="flex items-baseline gap-2 border-b border-amber-200/40 pb-1">
                            <span className="font-bold text-amber-900 whitespace-nowrap shrink-0">Ligne {err.row}</span>
                            {label && <span className="font-semibold text-amber-800 whitespace-nowrap shrink-0">{label}</span>}
                            <span className="text-amber-700">{err.error}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleImport}
                disabled={importing || !file || !classId || !schoolId || !academicYearId}
                className={cn(
                  'flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all shadow-md',
                  importing || !file || !classId || !schoolId || !academicYearId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-blue-200'
                )}
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />{lang === 'fr' ? 'Importation...' : 'Importing...'}</>
                ) : (
                  <><UserPlus className="w-4 h-4" />{lang === 'fr' ? 'Importer les élèves' : 'Import Students'}</>
                )}
              </button>
              <button
                onClick={resetForm}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all"
              >
                <X className="w-4 h-4" />
                {lang === 'fr' ? 'Réinitialiser' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}